"use client";
import { useState, useRef, useEffect, useMemo } from "react";
import styles from "./Table.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import Spinner from "../../../spinner";
import { parseDateSafe } from "../../../../utils/dateFormatters";
const PaginationButton = ({ onClick, disabled, loading, children, ariaLabel, active }) => {
  return (
    <button
      className={`${styles.paginationButton} ${active ? styles.activePaging : ""}`}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      type="button"
    >
      <span style={{ opacity: loading ? 0.85 : 1 }}>{children}</span>
    </button>
  );
};
const setsAreEqual = (aSet, bArr) => {
  if (!aSet && !bArr) return true;
  if (!aSet || !bArr) return false;
  if (aSet.size !== bArr.length) return false;
  for (const x of bArr) if (!aSet.has(x)) return false;
  return true;
};
const Table = ({
  columns,
  data,
  height,
  isRowClickable,
  onRowClick,
  loading,
  getRowClassName,
  showCheckboxes = false,
  onSelectionChange,
  selectedIds = [],
  horizontal = false,
  handleAction,
  rowColor,
  isRowDisabled = () => false,
  pagination = false,
  meta = null,
  onPageChange,
  paginationLoading = false,
  onSortChange,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "none" });
  const [selectedRows, setSelectedRows] = useState(new Set(selectedIds));
  const [allSelected, setAllSelected] = useState(false);
  const containerRef = useRef(null);
  const [dataChangedFlash, setDataChangedFlash] = useState(false);
  const [activePagingBtn, setActivePagingBtn] = useState(null);
  const tableData = useMemo(() => {
    if (pagination && data?.results) return Array.isArray(data.results) ? data.results : [];
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  }, [data, pagination]);
  const sortedData = useMemo(() => {
    const base = Array.isArray(tableData) ? tableData.slice() : [];
    if (!sortConfig.key) return base;
    const column = (columns || []).find((col) => col.key === sortConfig.key);
    if (!column) return base;
    base.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (column.render && typeof aValue !== "number" && typeof bValue !== "number") {
        try {
          aValue = column.render(a);
          bValue = column.render(b);
        } catch (e) {}
      }
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      const dateA = parseDateSafe(aValue);
      const dateB = parseDateSafe(bValue);
      if (dateA && dateB) {
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sortConfig.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    return base;
  }, [tableData, sortConfig, columns]);
  useEffect(() => {
    if (!setsAreEqual(selectedRows, selectedIds)) {
      setSelectedRows(new Set(selectedIds));
    }
  }, [selectedIds]);
  useEffect(() => {
    const newAll = sortedData.length > 0 && selectedRows.size === sortedData.length;
    if (newAll !== allSelected) setAllSelected(newAll);
  }, [selectedRows, sortedData, allSelected]);
  const rowIdsFingerprint = useMemo(() => {
    try {
      return tableData.map((r) => r?.id ?? r?.serial ?? r?.imei ?? "").join("|");
    } catch {
      return "";
    }
  }, [tableData]);
  useEffect(() => {
    if (!containerRef.current) return;
    setDataChangedFlash(true);
    const t = setTimeout(() => setDataChangedFlash(false), 600);
    return () => clearTimeout(t);
  }, [rowIdsFingerprint]);
  const triggerSortChange = (colKey, direction) => {
    if (typeof onSortChange !== "function") return;
    const col = (columns || []).find((c) => c.key === colKey);
    const backendField =
      col?.sortField ??
      colKey;
    if (!colKey || direction === "none") {
      onSortChange(null, null);
      return;
    }
    const order = direction === "asc" ? "asc" : "desc";
    onSortChange(backendField, order);
  };
  const handleSort = (key) => {
    setSortConfig((prev) => {
      let next;
      if (prev.key === key) {
        if (prev.direction === "asc") next = { key, direction: "desc" };
        else if (prev.direction === "desc") next = { key: null, direction: "none" };
        else next = { key, direction: "asc" };
      } else {
        next = { key, direction: "asc" };
      }
      if (next.key) triggerSortChange(next.key, next.direction);
      else triggerSortChange(null, "none");
      return next;
    });
  };
  const handleRowSelection = (id) => {
    setSelectedRows((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(id)) newSelected.delete(id);
      else newSelected.add(id);
      if (onSelectionChange) onSelectionChange(Array.from(newSelected));
      return newSelected;
    });
  };
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = sortedData.map((row, idx) => row?.id ?? row?.serial ?? row?.imei ?? `row-${idx}`);
      if (!setsAreEqual(selectedRows, allIds)) {
        setSelectedRows(new Set(allIds));
        onSelectionChange?.(allIds);
      }
      setAllSelected(true);
    } else {
      if (selectedRows.size !== 0) {
        setSelectedRows(new Set());
        onSelectionChange?.([]);
      }
      setAllSelected(false);
    }
  };
  const truncateText = (content) => {
    if (typeof content !== "string") return content;
    const words = content.split(" ");
    return words.length > 5 ? words.slice(0, 5).join(" ") + "..." : content;
  };
  const checkboxColumn = showCheckboxes
    ? [
        {
          key: "checkbox",
          label: (
            <label className={styles.checkboxLabel} onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                className={styles.customCheckbox}
                checked={allSelected && sortedData.length > 0}
                onChange={handleSelectAll}
              />
            </label>
          ),
          sorting: false,
          render: (row, rowIndex) => (
            <label className={styles.checkboxLabel} onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selectedRows.has(row?.id ?? row?.serial ?? row?.imei ?? `row-${rowIndex}`)}
                onChange={() => handleRowSelection(row?.id ?? row?.serial ?? row?.imei ?? `row-${rowIndex}`)}
                className={styles.customCheckbox}
              />
            </label>
          ),
        },
      ]
    : [];
  const allColumns = [...checkboxColumn, ...(columns || [])];
  const getMetaField = (m) => (keyCandidates) => {
    if (!m) return null;
    for (const k of keyCandidates) {
      if (k in m) return m[k];
    }
    return null;
  };
  const metaGetter = getMetaField(meta);
  const metaPrev = metaGetter(["previous", "prev", "previous_page", "prev_page", "previous_url"]);
  const metaNext = metaGetter(["next", "next_page", "next_url"]);
  const metaCurrentPage = metaGetter(["current_page", "page", "current", "page_number", "page_no"]);
  const metaTotalPages = metaGetter(["total_pages", "total_pages_count", "totalPages"]);
  const metaTotalItems = metaGetter(["total_items", "count", "total"]);
  const pagesLeft = (() => {
    const cur = Number.isFinite(Number(metaCurrentPage)) ? Number(metaCurrentPage) : null;
    const total = Number.isFinite(Number(metaTotalPages)) ? Number(metaTotalPages) : null;
    if (cur !== null && total !== null) return Math.max(0, total - cur);
    return null;
  })();
  const percentComplete = (() => {
    const cur = Number.isFinite(Number(metaCurrentPage)) ? Number(metaCurrentPage) : null;
    const total = Number.isFinite(Number(metaTotalPages)) ? Number(metaTotalPages) : null;
    if (cur !== null && total !== null && total > 0) {
      return Math.round((cur / total) * 100);
    }
    return null;
  })();
  const handlePageClick = (which) => {
    if (!onPageChange || !meta) return;
    setActivePagingBtn(which);
    const clearActive = setTimeout(() => setActivePagingBtn(null), 2000);
    if (which === "prev") {
      if (typeof metaPrev === "string" && metaPrev) {
        onPageChange(metaPrev);
        return;
      }
      if (Number.isFinite(metaCurrentPage)) {
        const prevPageNum = Number(metaCurrentPage) - 1;
        if (prevPageNum > 0) onPageChange(prevPageNum);
        return;
      }
      onPageChange(metaPrev ?? null);
    } else if (which === "next") {
      if (typeof metaNext === "string" && metaNext) {
        onPageChange(metaNext);
        return;
      }
      if (Number.isFinite(metaCurrentPage)) {
        const nextPageNum = Number(metaCurrentPage) + 1;
        if (!Number.isFinite(metaTotalPages) || nextPageNum <= Number(metaTotalPages)) {
          onPageChange(nextPageNum);
        }
        return;
      }
      onPageChange(metaNext ?? null);
    }
    setTimeout(() => clearTimeout(clearActive), 2100);
  };
  return (
    <div>
      {pagination && meta && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#fff",
              padding: "3px 6px",
              borderRadius: 12,
              minWidth: 160,
              maxWidth: 260,
              fontFamily: "inherit",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}></div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {metaCurrentPage ? `Page ${metaCurrentPage}` : "-"}
                  {metaTotalPages ? ` / ${metaTotalPages}` : ""}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.table_wrapper}>
        <div
          className={`${styles.table_container} ${dataChangedFlash ? styles.dataFlash : ""}`}
          style={{
            maxHeight: height ? `${height}px` : "auto",
            overflowX: "auto",
          }}
          ref={containerRef}
        >
          <table className={styles.full_table}>
            <thead className={styles.header_table}>
              <tr>
                {allColumns.map((col, colIndex) => (
                  <th
                    key={col.key ?? `col-${colIndex}`}
                    className={col.sorting === false ? styles.unsorted : ""}
                    style={{
                      textAlign: col.alignLeft ? "left" : "center",
                      minWidth:
                        col.key === "checkbox"
                          ? "60px"
                          : col.key === "name"
                          ? "180px"
                          : col.key === "provider"
                          ? "180px"
                          : col.key === "facility"
                          ? "120px"
                          : (col.key || "").includes("Date")
                          ? "180px"
                          : "100px",
                    }}
                    onClick={col.sorting === false ? null : () => handleSort(col.key)}
                  >
                    {typeof col.label === "function" ? col.label() : col.label}
                    {col.key !== "checkbox" && col.sorting !== false ? (
                      <FontAwesomeIcon
                        icon={sortConfig.key === col.key ? (sortConfig.direction === "asc" ? faSortUp : faSortDown) : faSort}
                        className={`${styles.sortIcon} ${sortConfig.key === col.key ? styles.active : ""}`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            {loading ? (
              <tbody className={styles.body_table}>
                <tr>
                  <td colSpan={allColumns.length} style={{ textAlign: "center" }}>
                    <Spinner />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className={styles.body_table}>
                {sortedData && sortedData.length > 0 ? (
                  sortedData.map((row, rowIndex) => {
                    const disabled = isRowDisabled(row);
                    const primaryId = row?.id ?? row?.serial ?? row?.imei ?? row?.tenant_id ?? row?.box?.id;
                    const rowKey = `${primaryId ?? "r"}-${rowIndex}`;
                    const isSelected = selectedRows.has(row?.id ?? row?.serial ?? row?.imei ?? rowKey);
                    return (
                      <tr
                        key={rowKey}
                        className={`${getRowClassName ? getRowClassName(row) : ""} ${isSelected ? styles.selected_row : ""} ${
                          disabled ? styles.disabled_row : ""
                        }`}
                        style={{
                          cursor: isRowClickable ? "pointer" : "default",
                          backgroundColor: rowColor ? rowColor(row) : "transparent",
                          ...(disabled ? { opacity: 0.5, backgroundColor: "#f5f5f5" } : {}),
                        }}
                        onClick={() => !disabled && onRowClick && onRowClick(row)}
                      >
                        {allColumns.map((col, colIndex) => {
                          const cellKey = `${col.key ?? `col-${colIndex}`}-${rowKey}-${colIndex}`;
                          const cellContent = col.render ? col.render(row, rowIndex) : row[col.key];
                          return (
                            <td key={cellKey} style={{ textAlign: col.alignLeft ? "left" : "center" }}>
                              {truncateText(cellContent)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={allColumns.length} style={{ textAlign: "center" }}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
        </div>
        {pagination && meta && (
          <div className={styles.paginationContainer}>
            <div className={styles.paginationMetaRight}>
              <div className={styles.smallMeta}>
                Total: <b>{metaTotalItems ?? "-"}</b>
              </div>
            </div>
            <div className={styles.paginationButtonsGroup}>
              <PaginationButton
                onClick={() => handlePageClick("prev")}
                disabled={!metaPrev || loading}
                loading={paginationLoading && activePagingBtn === "prev"}
                ariaLabel="Previous page"
                active={activePagingBtn === "prev"}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </PaginationButton>
              <span className={styles.paginationInfo}>
                Page <b>{metaCurrentPage ?? "-"}</b> of <b>{metaTotalPages ?? "-"}</b>
              </span>
              <PaginationButton
                onClick={() => handlePageClick("next")}
                disabled={!metaNext || loading}
                loading={paginationLoading && activePagingBtn === "next"}
                ariaLabel="Next page"
                active={activePagingBtn === "next"}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </PaginationButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Table;